import React, { useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  Skeleton,
  styled,
} from "@mui/material";
import { toast } from "react-hot-toast";
import QRCode from "react-qr-code";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

const CardRoot = styled(Box)(() => ({
  background: "#fff",
  borderRadius: 12,
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  border: "1px solid #F0F0F0",
  transition: "box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(162,89,255,0.12)",
    transform: "translateY(-1px)",
  },
}));

const QRThumbnail = styled(Box)({
  width: 72,
  height: 72,
  borderRadius: 8,
  overflow: "hidden",
  background: "#fff",
  border: "1px solid #E8E8E8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  padding: 4,
});

const ACTION_BUTTONS = [
  {
    key: "view",
    label: "view",
    Icon: VisibilityOutlinedIcon,
    bg: "#F5EEFF",
    color: "#A259FF",
    hoverBg: "#EDE0FF",
  },
  {
    key: "share",
    label: "share",
    Icon: ShareOutlinedIcon,
    bg: "#E8F5FF",
    color: "#478AFF",
    hoverBg: "#D6ECFF",
  },
  {
    key: "download",
    label: "download",
    Icon: FileDownloadOutlinedIcon,
    bg: "#EDFAF3",
    color: "#34C759",
    hoverBg: "#D8F5E6",
  },
  {
    key: "copy",
    label: "copy",
    Icon: ContentCopyOutlinedIcon,
    bg: "#FFFAED",
    color: "#F2A73B",
    hoverBg: "#FFF3D0",
  },
];

export const QRCardSkeleton = () => (
  <CardRoot>
    <Box display="flex" gap={2} alignItems="flex-start">
      <Skeleton variant="rounded" width={72} height={72} />
      <Box flex={1}>
        <Skeleton width="70%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton width="50%" height={16} />
      </Box>
    </Box>
    <Box display="flex" gap={1}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} variant="rounded" sx={{ flex: 1 }} height={36} />
      ))}
    </Box>
  </CardRoot>
);

const QRCard = ({ qr, onView, onShare, onCopy }) => {
  const qrRef = useRef(null);

  const handleDownload = useCallback(() => {
    const svgEl = qrRef.current?.querySelector("svg");
    if (!svgEl) return;

    const locationName =
      qr.locationId?.businessLocationName || qr.locationName || "location";
    const fileName = `${locationName.replace(/\s+/g, "_")}_qr.png`;

    const svgData = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const padding = 32;
      const size = 256 + padding * 2;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, padding, padding, 256, 256);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        URL.revokeObjectURL(svgUrl);
        toast.success("qr downloaded");
      }, "image/png");
    };
    img.src = svgUrl;
  }, [qr]);

  const handlers = { view: onView, share: onShare, download: handleDownload, copy: onCopy };

  return (
    <CardRoot>
      {/* Top: QR thumbnail + info */}
      <Box display="flex" gap={2} alignItems="center">
        <QRThumbnail>
          <QRCode value={qr.qrValue || " "} size={60} />
        </QRThumbnail>
        {/* Hidden full-res QR for PNG download */}
        <Box ref={qrRef} sx={{ display: "none" }}>
          <QRCode value={qr.qrValue || " "} size={256} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            fontWeight={600}
            fontSize={14}
            color="#1a1a1a"
            noWrap
            textTransform="lowercase"
            sx={{ fontFamily: "Inter, Arial, sans-serif", lineHeight: 1.4 }}
          >
            {qr.name}
          </Typography>
          {/* Pin icon + location name — kept on same baseline */}
          <Box
            display="flex"
            alignItems="flex-start"
            gap={0.3}
            mt={0.5}
          >
            <LocationOnOutlinedIcon
              sx={{ fontSize: 13, color: "#A259FF", flexShrink: 0, mt: "1px" }}
            />
            <Typography
              fontSize={12}
              color="#666"
              sx={{ fontFamily: "Inter, Arial, sans-serif", lineHeight: 1.4 }}
            >
              {qr.locationId?.businessLocationName || qr.locationName || qr.city}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action buttons — each stretches to fill row equally */}
      <Box display="flex" gap={0.75}>
        {ACTION_BUTTONS.map(({ key, label, Icon, bg, color, hoverBg }) => (
          <Tooltip key={key} title={label} placement="top">
            <Button
              onClick={() => handlers[key]?.(qr)}
              aria-label={label}
              sx={{
                flex: 1,
                minWidth: 0,
                py: 1,
                px: 0,
                borderRadius: "8px",
                backgroundColor: bg,
                color,
                "&:hover": { backgroundColor: hoverBg },
                "& svg": { fontSize: 20 },
              }}
            >
              <Icon />
            </Button>
          </Tooltip>
        ))}
      </Box>
    </CardRoot>
  );
};

export default QRCard;
